import React from 'react';
import AnchorOverrideControl from './AnchorOverrideControl';
import { Box, Flex, Text, Icon, Badge } from '@chakra-ui/react';
import { FiInfo, FiAlertTriangle } from 'react-icons/fi';
import dayjs from 'dayjs';
import { STATUS_LABEL } from './constants';

function fmtDate(iso) {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD-MM-YYYY') : '';
}

/**
 * Banda superior del modal. Cambia de tono según cuántas matrículas hay:
 *  - 1 matrícula → morada. Consolida el detalle de la matrícula (id, badge
 *    status, semestre, fecha creada) en el banner, así no se repite en cada
 *    tarjeta.
 *  - 2+ matrículas → ámbar. Instrucción: elegir en cada pago.
 */
export default function ContextBanner({
  enrollments = [],
  orphanCount,
  programName,
  studentName,
  enrollmentType,
  anchorOverride,
  anchorOverrideNote,
  anchorOverrideAt,
  anchorOverrideBy,
  anchorAuto,
  onAnchorChange,
  setAnchorOverride,
}) {
  const multi = enrollments.length > 1;
  const one = enrollments.length === 1 ? enrollments[0] : null;
  const bg = multi ? '#FFFBEB' : '#F5F3FF';
  const border = multi ? '#FCD34D' : '#DDD6FE';
  const color = multi ? '#92400E' : '#5B21B6';
  const IconEl = multi ? FiAlertTriangle : FiInfo;

  return (
    <Box bg={bg} borderBottomWidth="1px" borderColor={border} px={6} py={3}>
      <Flex align="flex-start" gap={3}>
        <Icon as={IconEl} boxSize="18px" color={color} mt="1px" />
        <Box flex="1">
          {multi ? (
            <>
              <Text fontSize="13px" fontWeight="700" color={color}>
                {enrollments.length} matrículas activas del estudiante en {programName || 'este programa'}
              </Text>
              <Text fontSize="12px" color={color} opacity={0.85} mt={0.5}>
                Elige la matrícula correcta en cada pago. La heurística sugiere una por fecha, pero puedes cambiarla.
              </Text>
            </>
          ) : one ? (
            <>
              <Text fontSize="11px" fontWeight="700" color={color} letterSpacing="0.04em" textTransform="uppercase">
                Se vinculará a
              </Text>
              <Flex align="baseline" gap={2} flexWrap="wrap" mt={0.5}>
                <Text fontSize="15px" fontWeight="700" color="gray.800">
                  Matrícula #{one.id}
                </Text>
                {one.status && (
                  <Badge
                    colorScheme={['estudiando', 'promocion_pendiente'].includes(one.status) ? 'green' : 'gray'}
                    fontSize="10px"
                    letterSpacing="0.04em"
                    px={2}
                    py={0.5}
                    borderRadius="3px"
                  >
                    {STATUS_LABEL[one.status] || one.status}
                  </Badge>
                )}
                {one.semesterName && (
                  <Text fontSize="12px" color="gray.700">
                    · semestre <strong>{one.semesterName}</strong>
                  </Text>
                )}
                {one.createdAt && (
                  <Text fontSize="12px" color="gray.500">
                    · creada {fmtDate(one.createdAt)}
                  </Text>
                )}
              </Flex>
              <Text fontSize="11px" color={color} opacity={0.75} mt={1} fontStyle="italic">
                Única matrícula activa del programa · {orphanCount} pago{orphanCount === 1 ? '' : 's'} por vincular
              </Text>
            </>
          ) : (
            <>
              <Text fontSize="13px" fontWeight="700" color={color}>
                {orphanCount} pago{orphanCount === 1 ? '' : 's'} por vincular
              </Text>
              <Text fontSize="12px" color={color} opacity={0.85} mt={0.5}>
                {studentName || 'Estudiante'} · asigna las materias o meses que cubre cada pago.
              </Text>
            </>
          )}
        </Box>
        {!multi && one && enrollmentType && setAnchorOverride && (
          <AnchorOverrideControl
            enrollmentType={enrollmentType}
            enrollmentId={one.id}
            override={anchorOverride}
            overrideNote={anchorOverrideNote}
            overrideAt={anchorOverrideAt}
            overrideBy={anchorOverrideBy}
            anchorAuto={anchorAuto}
            onChange={onAnchorChange}
            setAnchorOverride={setAnchorOverride}
          />
        )}
      </Flex>
    </Box>
  );
}
